import type { Notification } from '../../types/notification';
import { useUserStore } from '../../app/store/useUserStore';
import { getLogger } from './logger';

const logger = getLogger('notificationStorage');

const STORAGE_KEY = 'skyporter_notifications';
const MAX_NOTIFICATIONS = 30;
const DUPLICATE_PREVENTION_WINDOW = 2000;

class NotificationStorage {
  private lastNotificationTime: Map<string, number> = new Map();

  private getNotificationHash(notification: Omit<Notification, 'id' | 'timestamp'>): string {
    if ((notification.type === 'chat_message' || notification.type === 'order_group_chat') && notification.data?.message_id) {
      return `${notification.type}|${notification.data.message_id}`;
    }
    if (notification.data?.order_code) {
      return `${notification.type}|${notification.data.order_code}`;
    }
    return `${notification.title}|${notification.message}`;
  }

  private isDuplicate(notification: Omit<Notification, 'id' | 'timestamp'>): boolean {
    const hash = this.getNotificationHash(notification);
    const lastTime = this.lastNotificationTime.get(hash);
    const now = Date.now();

    if (lastTime && now - lastTime < DUPLICATE_PREVENTION_WINDOW) {
      logger.warn('addNotification: duplicate suppressed by hash window', {
        hash,
        msSinceLast: now - lastTime,
        type: notification.type,
        notification_id: notification.data?.notification_id,
      });
      return true;
    }

    this.lastNotificationTime.set(hash, now);
    return false;
  }

  getNotifications(): Notification[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      const notifications = JSON.parse(stored) as Notification[];

      return notifications;
    } catch {
      return [];
    }
  }

  private saveNotifications(notifications: Notification[], skipEvent = false): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
      if (!skipEvent) {
        setTimeout(() => window.dispatchEvent(new CustomEvent('notification-state-changed')), 0);
      }
    } catch { /* ignore */ }
  }

  private getCurrentUserInfo(): { userId?: string; user_role?: 'traveller' | 'sender' } {
    try {
      const currentUser = useUserStore.getState().user;
      if (!currentUser) return {};
      return {
        userId: currentUser.uuid || String(currentUser.user_id || ''),
        user_role: currentUser.user_role as 'traveller' | 'sender' | undefined,
      };
    } catch {
      return {};
    }
  }

  addNotification(notification: Omit<Notification, 'id' | 'timestamp'>): Notification | null {
    if (!notification.userId || !notification.user_role) {
      const userInfo = this.getCurrentUserInfo();
      if (!notification.userId && userInfo.userId) {
        notification = { ...notification, userId: userInfo.userId };
      }
      if (!notification.user_role && userInfo.user_role) {
        notification = { ...notification, user_role: userInfo.user_role };
      }
    }

    if (this.isDuplicate(notification)) {
      return null;
    }

    let notifications = this.getNotifications();

    if (notification.type !== 'chat_message' && notification.type !== 'order_group_chat' && notification.data?.order_code) {
      // require notification_id to also match so repeat reminders for the same order aren't collapsed into one
      // scope by user_role too, so a sender notification and a traveller notification for the same
      // order (e.g. when one account toggles roles) don't collide and show each other's wording
      const existing = notifications.find(
        (n) => n.type === notification.type
          && n.data?.order_code === notification.data!.order_code
          && n.user_role === notification.user_role
      );
      const differentNotificationId =
        existing?.data?.notification_id !== undefined &&
        notification.data?.notification_id !== undefined &&
        existing.data.notification_id !== notification.data.notification_id;
      if (
        existing &&
        !differentNotificationId &&
        existing.data?.statusFlag !== 'done' &&
        existing.data?.statusFlag !== 'rejected'
      ) {
        return existing;
      }
    }

    if ((notification.type === 'chat_message' || notification.type === 'order_group_chat') && notification.data?.chat_room_id) {
      const chatRoomId = notification.data.chat_room_id;

      const existingChatNotif = notifications.find(
        (n) => n.type === notification.type && n.data?.chat_room_id === chatRoomId
      );

      if (existingChatNotif) {
        existingChatNotif.data = {
          ...existingChatNotif.data,
          ...notification.data,
          last_message: notification.message,
          last_message_id: notification.data.message_id,
        };
        existingChatNotif.timestamp = Date.now();
        existingChatNotif.isRead = false;

        notifications = notifications.filter((n) => n.id !== existingChatNotif.id);
        notifications.unshift(existingChatNotif);

        this.saveNotifications(notifications);
        return existingChatNotif;
      }
    }

    const newNotification: Notification = {
      ...notification,
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
    };

    if (newNotification.type === 'chat_message' || newNotification.type === 'order_group_chat') {
      newNotification.data = {
        ...newNotification.data,
        last_message: notification.message,
      };
    }

    notifications.unshift(newNotification);

    const { userId, user_role } = this.getCurrentUserInfo();
    const userNotifs = notifications.filter(
      (n) => n.userId === userId && n.user_role === user_role
    );
    if (userNotifs.length > MAX_NOTIFICATIONS) {
      const toRemove = new Set(
        userNotifs.slice(MAX_NOTIFICATIONS).map((n) => n.id)
      );
      notifications = notifications.filter((n) => !toRemove.has(n.id));
    }

    this.saveNotifications(notifications);
    return newNotification;
  }

  markAsRead(notificationId: string, skipEvent = false): void {
    const notifications = this.getNotifications();
    const notification = notifications.find((n) => n.id === notificationId);

    if (notification && !notification.isRead) {
      notification.isRead = true;
      this.saveNotifications(notifications, skipEvent);
    }
  }

  markAllAsRead(skipEvent = false): void {
    const notifications = this.getNotifications();
    let hasChanges = false;

    notifications.forEach((notification) => {
      if (!notification.isRead) {
        notification.isRead = true;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      this.saveNotifications(notifications, skipEvent);
    }
  }

  getUnreadCount(): number {
    const notifications = this.getNotifications();
    return notifications.filter((n) => !n.isRead).length;
  }

  deleteNotification(notificationId: string, skipEvent = false): void {
    const notifications = this.getNotifications();
    const filtered = notifications.filter((n) => n.id !== notificationId);
    this.saveNotifications(filtered, skipEvent);
  }

  clearAll(): void {
    localStorage.removeItem(STORAGE_KEY);
    setTimeout(() => window.dispatchEvent(new CustomEvent('notification-state-changed')), 0);
  }

  private isDuplicateOfExisting(newNotif: Notification, existing: Notification[]): boolean {
    return existing.some((e) => {
      if (e.id === newNotif.id) return true;

      if (
        newNotif.data?.notification_id &&
        e.data?.notification_id === newNotif.data.notification_id
      ) {
        return true;
      }

      // these types can legitimately repeat per order_code with a new notification_id each time
      if (
        newNotif.type !== 'shipment_offer_request' &&
        newNotif.type !== 'offer_expiry_reminder' &&
        newNotif.type !== 'offer_expired' &&
        e.type === newNotif.type &&
        newNotif.data?.order_code &&
        e.data?.order_code === newNotif.data.order_code
      ) {
        return true;
      }

      return false;
    });
  }

  addMissedNotifications(missedNotifications: unknown[]): number {
    if (!missedNotifications || missedNotifications.length === 0) {
      return 0;
    }

    const currentNotifications = this.getNotifications();
    let addedCount = 0;

    const convertedNotifications: Notification[] = missedNotifications.map((backendNotif: any) => {
      const isGroupChat = backendNotif.type === 'order_group_chat' || backendNotif.data?.type === 'order_group_chat';
      const isChatMessage = !isGroupChat && (backendNotif.initiator_type === 'chat_message' || (backendNotif.data?.chat_room_id && backendNotif.type !== 'chat_room_created'));

      const currentUserInfo = this.getCurrentUserInfo();

      const recipientRole = currentUserInfo.user_role;

      const baseNotification: Notification = {
        id: `backend_${backendNotif.id}`,
        title: backendNotif.title,
        message: backendNotif.message,
        timestamp: new Date(backendNotif.created_at).getTime(),
        type: isGroupChat ? 'order_group_chat' : isChatMessage ? 'chat_message' : (backendNotif.type || backendNotif.initiator_type),
        isRead: backendNotif.is_read,
        userId: currentUserInfo.userId,
        user_role: recipientRole,
        data: {
          ...backendNotif.data,
          notification_id: backendNotif.id,
          statusFlag: backendNotif.is_read ? 'done' : 'pending',
          ...(backendNotif.chat_room_id && { chat_room_id: String(backendNotif.chat_room_id) }),
          ...(backendNotif.chat_roomId != null && { chat_roomId: backendNotif.chat_roomId }),
          ...(backendNotif.order_code && { order_code: backendNotif.order_code }),
          ...(backendNotif.sender && { sender: backendNotif.sender }),
        },
      };

      return baseNotification;
    });

    convertedNotifications.forEach((newNotif) => {
      if ((newNotif.type === 'chat_message' || newNotif.type === 'order_group_chat') && newNotif.data?.chat_room_id) {
        const existingIndex = currentNotifications.findIndex(
          (n) => n.type === newNotif.type && n.data?.chat_room_id === newNotif.data?.chat_room_id
        );

        if (existingIndex !== -1) {
          const existing = currentNotifications[existingIndex];

          if (newNotif.isRead === false) {
            existing.data = {
              ...existing.data,
              ...newNotif.data,
            };
            existing.isRead = false;
            existing.timestamp = Math.max(existing.timestamp, newNotif.timestamp);

            currentNotifications.splice(existingIndex, 1);
            currentNotifications.unshift(existing);
            addedCount++;
          }
          return;
        }
      }

      if (!this.isDuplicateOfExisting(newNotif, currentNotifications)) {
        if (
          newNotif.data?.order_code &&
          newNotif.data?.statusFlag === 'pending' &&
          currentNotifications.some(
            (e) =>
              e.data?.order_code === newNotif.data?.order_code &&
              (e.data?.statusFlag === 'done' || e.data?.statusFlag === 'rejected')
          )
        ) {
          return;
        }
        currentNotifications.unshift(newNotif);
        addedCount++;
      } else {
        const existing = currentNotifications.find(
          (e) =>
            e.id === newNotif.id ||
            (newNotif.data?.notification_id && e.data?.notification_id === newNotif.data.notification_id) ||
            (newNotif.type !== 'shipment_offer_request' &&
              newNotif.type !== 'offer_expiry_reminder' &&
              newNotif.type !== 'offer_expired' &&
              e.data?.order_code && e.data.order_code === newNotif.data?.order_code && e.type === newNotif.type)
        );
        if (existing) {
          if (existing.data?.statusFlag === 'done' || existing.data?.statusFlag === 'rejected') {
          } else if (newNotif.isRead) {
            existing.isRead = true;
            existing.data = { ...existing.data, statusFlag: 'done' };
            addedCount++;
          }
        }
      }
    });

    if (addedCount === 0) {
      return 0;
    }

    const mergedNotifications = currentNotifications
      .sort((a, b) => b.timestamp - a.timestamp);

    this.saveNotifications(mergedNotifications);

    return addedCount;
  }

}

export const notificationStorage = new NotificationStorage();
